<template>
	<div>
		<div class="d-flex align-center mb-4 w-100">
			<v-btn
				class="normal-spacing mr-3"
				color="#b0d7fe60"
				theme="dark"
				variant="elevated"
				@click="exportCsv"
				v-if="canAction('read')">
				<v-icon class="mr-2">mdi-export</v-icon>
				Export CSV
			</v-btn>
			<v-btn
				color="#958fc530"
				theme="dark"
				variant="elevated"
				@click="triggerFileInput"
				v-if="canAction('update')"
				class="normal-spacing">
				<v-icon class="mr-2">mdi-import</v-icon>
				Import CSV
			</v-btn>
			<!-- Hidden file input for import -->
			<input ref="fileInput" type="file" accept=".csv" style="display: none" @change="handleImportFile" />
		</div>

		<v-alert v-if="error" type="error" dismissible class="w-100 mb-4" variant="tonal">
			{{ error }}
		</v-alert>

		<!-- SEARCH BAR -->
		<v-text-field
			v-model="search"
			density="compact"
			prepend-inner-icon="mdi-magnify"
			label="Search"
			hide-details
			clearable
			clear-icon="mdi-close-circle"></v-text-field>

		<v-data-table-server
			:headers="headers"
			:items="items"
			:itemsLength="total ?? 0"
			v-model:options="options"
			:items-per-page="options.itemsPerPage"
			:items-per-page-options="[
				{ value: 10, title: '10' },
				{ value: 25, title: '25' },
				{ value: 50, title: '50' },
				{ value: 100, title: '100' },
			]"
			theme="dark"
			class="rounded-1"
			style="background-color: #0e0818; border: 1px solid #fefefe20">
			<!-- Default row rendering: for each header, if the value is an array, render chips -->
			<template #item="{ item }">
				<tr @click="goToRecord(item)" class="pointer">
					<td v-for="header in headers" :key="header.key">
						<!-- If this is the 'actions' column, leave it empty so that the named slot is used -->
						<template v-if="header.key === 'actions'">
							<div class="w-100 d-flex align-center justify-end">
								<v-menu>
									<template #activator="{ props }">
										<v-btn icon v-bind="props" variant="plain" color="white">
											<v-icon>mdi-dots-vertical</v-icon>
										</v-btn>
									</template>
									<v-list min-width="150px">
										<v-list-item v-if="canAction('read')" :href="viewRecordUrl(item)" link>
											<v-list-item-title>View</v-list-item-title>
										</v-list-item>
										<v-list-item v-if="canAction('update')" :href="editRecordUrl(item)" link>
											<v-list-item-title>Edit</v-list-item-title>
										</v-list-item>
										<v-list-item v-if="canAction('delete')" link @click="deleteItem(item)">
											<v-list-item-title>Delete</v-list-item-title>
										</v-list-item>
										<v-list-item
											v-if="!canAction('update') && !canAction('delete') && !canAction('read')">
											<v-list-item-title>
												You don't have permissions to manage this model.
											</v-list-item-title>
										</v-list-item>
									</v-list>
								</v-menu>
							</div>
						</template>
						<template v-else-if="['createdAt', 'updatedAt', 'lockedAt', 'unlockedAt'].includes(header.key)">
							{{
								item[camelToSlug(header.key)] !== null
									? formatDate(item[camelToSlug(header.key)])
									: "N/A"
							}}
						</template>
						<template v-else-if="Array.isArray(item[camelToSlug(header.key)])">
							<v-chip
								v-for="(relation, index) in item[camelToSlug(header.key)]"
								:key="index"
								size="x-small"
								class="ma-1">
								{{ relation.name ?? relation.title ?? relation.id ?? relation }}
							</v-chip>
						</template>
						<template v-else-if="item[camelToSlug(header.key)]?.id">
							<v-chip size="small" class="ma-1" color="blue-lighten-4">
								{{
									item[camelToSlug(header.key)].name ??
									item[camelToSlug(header.key)].title ??
									item[camelToSlug(header.key)].business_name ??
									item[camelToSlug(header.key)].full_name ??
									item[camelToSlug(header.key)].email ??
									item[camelToSlug(header.key)].formattedPrice ??
									item[camelToSlug(header.key)].id ??
									item[camelToSlug(header.key)]
								}}
							</v-chip>
						</template>
						<!-- handle dates -->
						<template v-else-if="header.type === 'date'">
							{{ formatDate(item[camelToSlug(header.key)]) }}
						</template>
						<template v-else>{{ item[camelToSlug(header.key)] ?? header.key }}</template>
					</td>
				</tr>
			</template>
		</v-data-table-server>
	</div>
</template>

<script>
	import axios from "axios"

	export default {
		name: "AdminModuleRecords",
		props: {
			headers: { type: Array, default: () => [] },
			module: { type: Object, default: () => ({}) },
			user: { type: Object, default: () => ({}) },
			basePath: { type: String, default: "" },
		},
		data() {
			return {
				items: [],
				total: 0,
				search: "",
				error: null,
				options: {
					page: 1,
					itemsPerPage: 10,
					sortBy: [],
					sortDesc: [],
				},
				searchTimer: null,
			}
		},
		mounted() {
			const params = new URLSearchParams(window.location.search)
			if (params.has("search")) {
				this.search = params.get("search")
			}
			this.fetchData()
		},
		watch: {
			"options.page"(newPage, oldPage) {
				if (newPage !== oldPage) {
					this.fetchData()
				}
			},
			options: {
				handler() {
					this.fetchData()
				},
				deep: true,
			},
			search(value) {
				if (this.searchTimer) clearTimeout(this.searchTimer)
				this.searchTimer = setTimeout(() => {
					this.options.page = 1
					this.fetchData()
				}, 400)
			},
		},
		computed: {
			camelToSlug() {
				return (str) => {
					return str
						.replace(/([a-z])([A-Z])/g, "$1_$2")
						.replace(/[\s_]+/g, "_")
						.toLowerCase()
				}
			},
		},
		methods: {
			formatDate(dateString) {
				if (!dateString) return ""
				return new Date(dateString).toLocaleString()
			},
			canAction(actionName) {
				const userRoles = this.user.roles.map((role) => role.name)
				return this.module.config.permissions[actionName].some((role) => userRoles.includes(role))
			},
			async fetchData() {
				try {
					const page = this.options.page
					const itemsPerPage = this.options.itemsPerPage
					// Safely extract sortBy value:
					let sortBy = null
					if (this.options.sortBy && this.options.sortBy.length > 0) {
						const firstSort = this.options.sortBy[0]
						if (typeof firstSort === "object") {
							sortBy = firstSort.value || firstSort.key || ""
						} else {
							sortBy = firstSort
						}
					}
					const sortDesc =
						this.options.sortDesc && this.options.sortDesc.length > 0 ? this.options.sortDesc[0] : false

					// Build query parameters
					let url = `/${this.basePath}?page=${page}&itemsPerPage=${itemsPerPage}`
					if (sortBy) url += `&sortBy=${sortBy}`
					url += `&sortDesc=${sortDesc}`
					if (this.search) url += `&search=${encodeURIComponent(this.search)}`

					const { data } = await axios.get(url)
					this.items = data.data
					this.total = data.total
					if (data.current_page && data.current_page !== page) {
						this.options.page = data.current_page
					}
				} catch (err) {
					this.error =
						err.response?.data?.message || err.response?.error || err.message || "Error fetching data."
					console.error(err)
				}
			},
			goToRecord(item) {
				window.location = this.viewRecordUrl(item)
			},
			viewRecordUrl(item) {
				return `/${this.basePath}/${item.id}`
			},
			editRecordUrl(item) {
				return `/${this.basePath}/${item.id}/edit`
			},

			deleteItem(item) {
				if (!confirm("Are you sure?")) return
				axios
					.delete(`/${this.basePath}/${item.id}`)
					.then(() => this.fetchData())
					.catch((err) => console.error(err))
			},
			exportCsv() {
				window.location = `/${this.basePath}/export`
			},
			async handleImportFile(e) {
				const file = e.target.files[0]
				if (!file) return
				try {
					const formData = new FormData()
					formData.append("csv", file)
					const res = await axios.post(`/${this.basePath}/import`, formData, {
						headers: { "Content-Type": "multipart/form-data" },
					})
					alert("Import successful.")
					this.fetchData()
				} catch (err) {
					this.error =
						err.response?.data?.message ||
						err.response?.error ||
						err.response?.data?.error ||
						err.message ||
						"Error importing CSV."
				} finally {
					e.target.value = null
				}
			},
			triggerFileInput() {
				this.$refs.fileInput.click()
			},
		},
	}
</script>

<style scoped>
	.error-text {
		color: red;
	}
</style>
